import { ModalBaseComponent } from '@/components/base/modal-base/modal-base.component';
import { DocAddWidgetComponent } from '@/components/doc-add-widget/doc-add-widget.component';
import { InputComponent } from '@/components/ui-forms/input/input.component';
import { SelectComponent } from '@/components/ui-forms/select/select.component';
import { DictionaryCacheService } from '@/services/dictionary-cache.service';
import { NgIf, NgTemplateOutlet } from '@angular/common';
import { Component, Inject, OnInit, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule, UntypedFormBuilder, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import {
  DocumentSummary,
  PropertiesBookletService,
  PropertyBookletInsulation,
  PropertyBookletInsulationDetail,
  PropertyBookletInsulationWindowDetail,
  PropertyBookletType,
  RealProperty
} from '@eemo/api-client';
import { Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { PropertyBookletInsulationType } from '../property-booklet-insulation-card/property-booklet-insulation-card.component';
import { PropertyBookletInsulationService } from '../property-booklet-insulation-card/property-booklet-insulation.service';

export class PropertyBookletInsulationRoofDialogData {
  prop: RealProperty;
  bookletUid: string;
  bookletType: PropertyBookletType;
  type: PropertyBookletInsulationType;
}

@Component({
  selector: 'app-property-booklet-insulation-dialog',
  standalone: true,
  imports: [
    ModalBaseComponent,
    NgIf,
    NgTemplateOutlet,
    MatDialogModule,
    ReactiveFormsModule,
    DocAddWidgetComponent,
    InputComponent,
    SelectComponent,
    MatFormFieldModule
  ],
  templateUrl: './property-booklet-insulation-dialog.component.html',
  styleUrl: './property-booklet-insulation-dialog.component.css'
})
export class PropertyBookletInsulationDialogComponent implements OnInit {

  private readonly propertiesBookletService = inject(PropertiesBookletService);

  bookletUid: string;
  isBookletRenovation = false;
  prop: RealProperty;
  sub = new Subscription();
  arePopulateData = false;

  title: string;
  type: PropertyBookletInsulationType;
  isLoading = false;
  PropertyBookletInsulationType = PropertyBookletInsulationType;

  insulationTypeCodeList = [];

  get form_installationDate() {
    return this.form.get('installationDate') as FormControl<string>;
  }
  get form_typeCode() {
    return this.form.get('typeCode') as FormControl<string>;
  }
  get form_thickness() {
    return this.form.get('thickness') as FormControl<number>;
  }
  get form_thermalResistance() {
    return this.form.get('thermalResistance') as FormControl<number>;
  }
  get form_productSheet() {
    return this.form.get('productSheet') as FormControl<DocumentSummary>;
  }
  get form_installationDetails() {
    return this.form.get('installationDetails') as FormControl<DocumentSummary>;
  }
  get form_thermalCoefficient() {
    return this.form.get('thermalCoefficient') as FormControl<number>;
  }

  form = this.fb.group({
    installationDate: [null],
    typeCode: [],
    thickness: [],
    thermalResistance: [],
    thermalCoefficient: [],
    productSheet: [null, Validators.required],
    installationDetails: []
  });

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: PropertyBookletInsulationRoofDialogData,
    private fb: UntypedFormBuilder,
    public dialogRef: MatDialogRef<PropertyBookletInsulationDialogComponent>,
    private dictCacheService: DictionaryCacheService
  ) {
    this.prop = data.prop;
    this.bookletUid = data.bookletUid;
    this.isBookletRenovation = data.bookletType===PropertyBookletType.RENOVATION;
    this.type = data.type;
    this.title = PropertyBookletInsulationService.getTitle(data.type);

    this.initForm();
  }
  ngOnInit(): void {
  }

  initForm() {
    const code = this.type === PropertyBookletInsulationType.windows ? environment.WINDOWS_CODE : environment.INSULATION_CODE;
    this.dictCacheService.attributes[code].availableValues.forEach((value) => {
      this.insulationTypeCodeList.push({
        code: value.code,
        display: value.display.name
      });
    });

    this.populateData()
  }

  populateData() {
    this.isLoading = true;

    this.sub = this.propertiesBookletService.getRealPropertyBookletInsulation(this.prop.uid, this.bookletUid).subscribe({
      next: (resp) => {
        this.isLoading = false;
        this.setFormFromData(resp);
      },
      error: () => {
        this.isLoading = false;
      },
      complete: () => {
        this.arePopulateData = true;
      }
    });
  }

  setFormFromData(resp: PropertyBookletInsulation) {
    this.setValidators();
    const THERMAL_COEF_KEY: keyof PropertyBookletInsulationWindowDetail = 'thermalCoefficient';
    const THERMAL_RESISTANCE_KEY: keyof PropertyBookletInsulationDetail = 'thermalResistance';

    const key = resp?.[this.type] ?? null;
    this.form_installationDate.setValue(key?.installationDate ?? null);
    this.form_typeCode.setValue(key?.typeCode ?? null);
    key?.productSheet ? this.form_productSheet.setValue(key.productSheet) : null;
    key?.installationDetails ? this.form_installationDetails.setValue(key.installationDetails) : null;

    if (!key) {
      this.form_thermalCoefficient.setValue(null);
      this.form_thermalResistance.setValue(null);
      this.form_thickness.setValue(null);
      return;
    }

    if (THERMAL_COEF_KEY in key) {
      this.form_thermalCoefficient.setValue(key?.thermalCoefficient ?? null);
    }
    if (THERMAL_RESISTANCE_KEY in key) {
      this.form_thermalResistance.setValue(key?.thermalResistance ?? null);
      this.form_thickness.setValue(key?.thickness ?? null);
    }
  }

  setValidators() {
    if (this.isBookletRenovation) {
      this.form_installationDate.addValidators(Validators.required);
    }
  }

  onProductSheetDocSelected(newDoc: DocumentSummary) {
    this.form_productSheet.setValue(newDoc)
  }

  onProductSheetDocDeleted() {
    this.form_productSheet.setValue(null);
  }

  onInstallationDetailsDocSelected(newDoc: DocumentSummary) {
    this.form_installationDetails.setValue(newDoc)
  }

  onInstallationDetailsDocDeleted() {
    this.form_installationDetails.setValue(null);
  }

  onSubmit() {
    if (this.form.valid) {
      this.update();
    }
  }

  update() {
    this.isLoading = true;
    const body = this.getBody();
    this.sub = this.propertiesBookletService.updateRealPropertyBookletInsulation(this.prop.uid, this.bookletUid, body).subscribe({
      next: () => {
        this.isLoading = false;
        this.dialogRef.close(true);
      },
      error: (err) => {
        this.isLoading = false;
      },
    });
  }
  getBody(): PropertyBookletInsulation {
    const body = {};
    body[this.type] = {
        installationDate: this.form_installationDate.value,
        typeCode: this.form_typeCode.value,
        thickness: this.form_thickness.value,
        thermalResistance: this.form_thermalResistance.value,
        productSheet: this.form_productSheet.value,
        installationDetails: this.form_installationDetails.value,
    }
    if (this.type === PropertyBookletInsulationType.windows) {
      body[this.type].thermalCoefficient = this.form_thermalCoefficient.value;
    } else {
      body[this.type].thermalResistance = this.form_thermalResistance.value;
      body[this.type].thickness = this.form_thickness.value;
    }
    return body;
  }
}
